import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import * as ReactDOM from 'react-dom';
import LeafletMap from '../../../components/base/Map/LeafletMap';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import {
  getFilterData,
  getPaginationOpts,
  getResponseError,
  responseLogWithNotification,
} from '../../../utils/utils';
import { getVehicleRegistryData } from '../reducer';
import { FullScreenControl } from '../../../components/base/Map/FullScreenControl';
import RectangleDrawer from '../../../components/base/Map/RectangleDrawer';
import { loadVehicleRegistryList, loadVehicleTrips, MAP_EVENT } from '../actions';
import { VEHICLES_REGISTRY_MODULE } from '../../../configs/moduleNames';
import { PAGE_TYPE } from '../../FilterTemplate/components/filter-settings';
import FilterModal from '../../FilterTemplate/components/FilterModal';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import { vehicleMapSearchColumns, vehicleRegistryColumns } from './settings';
import Pagination from '../../../components/base/Pagination/Pagination';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import Loader from '../../../components/base/Loader/Loader';
import VehicleFilterRow from './VehicleFilterRow';
import getDefaultSort from '../../../utils/getDefaultSort';
import PointMarker from '../../../components/base/Map/PointMarker';
import { dateFormatter } from '../../../utils/formatters';
import { errorNotification } from '../../Notifications/notificationDecorator';
import MobileVehicleFooter from './MobileVehicleFooter';
import PropertyGrid from '../../../components/base/Grid/PropertyGrid';

export default function MobileVehiclesSearchMap(props) {
  const { handleOpenDetails } = props;

  const {
    vehicleRegistryList,
    loadingVehicleRegistryList,
    vehicleRegistryListMeta,
    vehicleTrips,
  } = useSelector(getVehicleRegistryData);

  const filterName = 'vehicle-search-registry-filter';
  const defaultFilterValues = {
    vehicle_datetime_at: [
      moment().subtract(1, 'd').format('YYYY-MM-DDTHH:mm:ss'),
      moment().format('YYYY-MM-DDTHH:mm:ss'),
    ],
  };
  const map = useRef(null);
  const [fullScreen, setFullScreen] = useState(false);
  const [bound, setBound] = useState(null);
  const [selected, setSelected] = useState(null);
  const [isOpenFilter, setOpenFilter] = useState(false);
  const dispatch = useDispatch();
  const store = useStore();
  const {
    getValues, watch, control, reset, setValue,
  } = useForm({ defaultValues: getFilterData(filterName, defaultFilterValues) });

  const getBound = () => {
    const latitude_from = Number(getValues('latitude_from'));
    const latitude_to = Number(getValues('latitude_to'));
    const longitude_from = Number(getValues('longitude_from'));
    const longitude_to = Number(getValues('longitude_to'));
    if (
      map?.current && (
        Number.isNaN(latitude_from) || !latitude_from
        || Number.isNaN(latitude_to) || !latitude_to
        || Number.isNaN(longitude_from) || !longitude_from
        || Number.isNaN(longitude_to) || !longitude_to
      )
    ) {
      const bound = map?.current.getBounds();
      const southWest = bound.getSouthWest();
      const northEast = bound.getNorthEast();
      return {
        point1: { lat: northEast.lat, lon: northEast.lng },
        point2: { lat: southWest.lat, lon: southWest.lng },
      };
    }
    return {
      point1: { lat: latitude_from, lon: longitude_from },
      point2: { lat: latitude_to, lon: longitude_to },
    };
  };

  const loadData = opts => {
    const paginationOpts = getPaginationOpts(store.getState()[VEHICLES_REGISTRY_MODULE].vehicleRegistryListMeta, opts);
    const bound = getBound();
    const requestData = {
      ...paginationOpts,
      filter: {
        ...getValues(),
        latitude_from: bound.point2.lat,
        latitude_to: bound.point1.lat,
        longitude_from: bound.point2.lon,
        longitude_to: bound.point1.lon,
      },
      sort,
    };
    requestData.filter.created_at = [...requestData.filter.vehicle_datetime_at];
    delete requestData.filter.vehicle_datetime_at;

    const p1 = dispatch(loadVehicleRegistryList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };
  const setMap = m => {
    map.current = m;
    loadData();
  };

  const [sort, setSort] = useState(getDefaultSort(vehicleRegistryColumns));
  useEffect(() => {
    const subscription = watch(values => {
      localStorage.setItem(filterName, JSON.stringify(values));
      loadData({ page: 1 });
    });
    return () => subscription.unsubscribe();
  }, [watch]);
  useEffect(() => { loadData(); }, [sort]);

  const handleChangeRectangle = polygon => {
    if (
      !polygon
      || !polygon[0][0]
      || !polygon[0][1]
      || !polygon[1][0]
      || !polygon[0][1]
    ) {
      return;
    }
    setValue('latitude_from', polygon[0][0]);
    setValue('latitude_to', polygon[1][0]);
    setValue('longitude_from', polygon[0][1]);
    setValue('longitude_to', polygon[1][1]);
  };

  const handleShowTrips = item => {
    setSelected(item);
    const requestData = {
      filter: {
        ...getValues(),
        vehicle_id: [item.id],
      },
    };
    const p1 = dispatch(loadVehicleTrips(requestData)).catch(response => errorNotification('Загрузка данных', getResponseError(response)));
  };

  const changeSort = fieldName => {
    if (sort && (sort === fieldName || sort === `-${fieldName}`)) {
      if (sort === `-${fieldName}`) {
        setSort(fieldName);
      } else if (sort === fieldName) {
        setSort('');
      }
    } else {
      setSort(`-${fieldName}`);
    }
  };

  const renderContextMenu = item => (
    <div className="more-wrapper">
      <Button variant="link" className="w-100 more-button" onClick={() => handleOpenDetails(item)}>
        Посмотреть
      </Button>
    </div>
  );

  const moreFormatter = (_, item) => (
    <TooltipWithPortal
      content={<Icons customIcon icon="more-vert" className="more-vert" />}
      classNamePortal="more-actions"
      tooltipContent={renderContextMenu(item)}
      classNameWrapper="more-tooltip-wrapper"
      className="more-tooltip"
    />
  );

  const renderFooter = () => {
    const element = document.getElementById('app-content-footer');
    if (!element) {
      return null;
    }
    return ReactDOM.createPortal(<MobileVehicleFooter
      control={control}
      defaultFilterValues={defaultFilterValues}
      getValues={getValues}
      setValue={setValue}
      reset={reset}
      page={PAGE_TYPE.VEHICLE_REGISTRY}
      hidePeriod
      showInterval
    />, element);
  };

  return (
    <>
      <div className="page-content-wrapper">
        <Loader global loading={loadingVehicleRegistryList} />
        <div className="page-content">
          <PropertyGrid
            data={vehicleRegistryList}
            columns={vehicleMapSearchColumns}
            name="vehicle-serarch-list"
            loading={loadingVehicleRegistryList}
            additionalColumns={[]}
            changeSort={changeSort}
            sort={sort}
            onDoubleClickByRow={handleShowTrips}
            settingsFormatter={moreFormatter}
            renderContextMenu={renderContextMenu}
            rowClassFormatter={i => (i.id === selected?.id ? 'clickable-table-row selected' : 'clickable-table-row')}
          />
          <Pagination meta={vehicleRegistryListMeta} list={vehicleRegistryList} handleChangeMeta={loadData} />
        </div>
      </div>
      <div className={classNames('mobile-map-box', { visible: !!selected })}>
        <div className="map-wrapper">
          <LeafletMap classNames="stops-map" setMap={setMap} onMove={loadData} onMoveEnd={loadData}>
            {vehicleTrips && vehicleTrips.length > 0 ? vehicleTrips.map((trip, key) => {
              const lat = Number(trip.vehicle_latitude);
              const lon = Number(trip.vehicle_longitude);
              if (Number.isNaN(lat) || Number.isNaN(lon)) return null;
              const coords = [lat, lon];
              return (
                <PointMarker
                  key={`circle-${key}`}
                  center={coords}
                  text={dateFormatter(trip.vehicle_datetime_at)}
                />
              );
            }) : null}

            <FullScreenControl onClick={() => setSelected(null)} fullScreen={!!selected} />
            <RectangleDrawer onChange={handleChangeRectangle} getValues={getValues} bound={bound} watch={watch} />
          </LeafletMap>
        </div>
      </div>
      {isOpenFilter
        ? (
          <FilterModal
            control={control}
            onClose={() => setOpenFilter(false)}
            getValues={getValues}
            reset={reset}
            defaultValues={defaultFilterValues}
            page={PAGE_TYPE.VEHICLE_REGISTRY}
          />
        )
        : null}
      {isMobile ? renderFooter() : null}
    </>

  );
}
